import { observable, action, computed, makeAutoObservable } from 'mobx';
import { debounce } from 'lodash';
import type { DebouncedFunc } from 'lodash';
import { DateTime } from 'src_common/utils/time/time';
import { generateCsv, mkConfig, download } from 'export-to-csv';
// eslint-disable-next-line no-restricted-imports
import { apiCommon } from 'src/api/ApiCommon';
import {
    AssetFlowsDataItemType,
    AssetFlowsParamsType,
    AssetFlowsResponseDataType,
} from 'src/api/config/accounts/getAssetFlows';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { createGuard } from 'src_common/common/createGuard';
import { SpecialColumnType } from 'src/components/CustomersBar/CustomerTab/TransactionHistoryTab/TransactionsList/TransactionsList';
import * as t from 'io-ts';
import { getCurrency, SdkStaff } from 'src/AppState/sdk/staff';
import { Amount } from 'src_common/common/amount/Amount';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';

interface CsvDataItemType {
    balanceAfter: string;
    balanceDelta: string;
    currency: string;
    date: string;
    description?: null | undefined | string;
    finalizedAt?: null | undefined | string;
    freebetCredits: Array<{
        amount: number;
        id: number;
    }>;
    gameName?: null | undefined | string;
    id: number;
    paymentMethod?:
        | null
        | undefined
        | {
              type: string; //PaymentMethodType,
          };
    paymentResponse?:
        | null
        | undefined
        | {
              providerOrderId: string;
          };
    status: string;
    transactionId: number;
    type: string;
}

interface CsvDataType {
    date: string;
    transactionId: number;
    betId: number;
    method: string;
    type: string;
    game: string;
    status: string;
    amount: string;
    balance: string;
    finalizedAt: string;
    orderId: string | null | undefined;
    bonus: string | null;
}

const METHOD_LABELS = {
    'credit-card': 'Debit card',
    'bank-transfer': 'Bank transfer',
    cash: 'Cash',
    cheque: 'Cheque',
    manual: 'Manual',
    'casino-bonus': 'Casino bonus',
    'open-banking': 'Open banking',
};

export interface RangeType {
    startDate: Date;
    endDate: Date;
}

interface FilterType {
    status: string;
    type: string;
}

const initialStartDate = DateTime.current().format('YYYY-MM-DDT00:00:00');
const initialEndDate = DateTime.current().format('YYYY-MM-DDT23:59:59');

class PageState {
    @observable public page: number = 1;

    @action public resetPages = (): void => {
        this.page = 1;
    };

    public constructor() {
        makeAutoObservable(this);
    }
}

type TabType = 'all' | 'out' | 'into';

const TabIO = t.union([t.literal('all'), t.literal('out'), t.literal('into')]);
const isTab = createGuard(TabIO);

export type AssetFlowsResponseType = AssetFlowsResponseDataType;
export type AssetFlowsItemType = AssetFlowsDataItemType;
export class TransactionHistoryTabState {
    public readonly perPage: number = 20;
    public readonly pagePerTab: MobxMapAutoNew<TabType, PageState> = new MobxMapAutoNew(() => new PageState());
    private readonly updateFinalRangeDebounced: DebouncedFunc<(range: RangeType) => void>;

    @observable public range: RangeType = {
        startDate: new Date(initialStartDate),
        endDate: new Date(initialEndDate),
    };

    @observable public finalRange: RangeType = {
        startDate: new Date(initialStartDate),
        endDate: new Date(initialEndDate),
    };

    @observable public filterType: FilterType = {
        status: 'all',
        type: 'all',
    };

    @observable public transactionTypeActiveFilter: string | undefined = 'all';
    @observable public transactionStatusActiveFilter: string | undefined = 'all';

    @observable.ref public userAssetFlows: AutoMap<
        [number, string, string, string | undefined, string | undefined, 'incoming' | 'outgoing' | undefined],
        Resource<AssetFlowsResponseType>
    >;

    public constructor(
        private readonly userId: number,
        public readonly routerBackoffice: RouterBackoffice,
        private readonly sdkStaff: SdkStaff
    ) {
        this.updateFinalRangeDebounced = debounce(this.setFinalRange, 300);

        this.userAssetFlows = new AutoMap(
            ([page, startDate, endDate, transactionType, transactionStatus, operationKind]) => {
                return new Resource(async (): Promise<AssetFlowsResponseType> => {
                    const body = {
                        userId,
                        perPage: this.perPage,
                        page,
                        startDate:
                            DateTime.from(startDate)?.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ss') ??
                            initialStartDate,
                        endDate:
                            DateTime.from(endDate)?.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ss') ?? initialEndDate,
                        type: transactionType,
                        status: transactionStatus,
                        operationKind,
                    } satisfies AssetFlowsParamsType;

                    const response = await apiCommon.getAssetFlows.run(body);

                    return response;
                }, 30_000);
            }
        );
        makeAutoObservable(this);
    }

    @action public handleOnUpdateDate = (startDate: Date, endDate: Date): void => {
        this.range = { startDate, endDate };
        this.updateFinalRangeDebounced({ startDate, endDate });
    };

    @action private setFinalRange = (range: RangeType): void => {
        this.finalRange = range;
        this.pageState.resetPages();
    };

    @computed public get currentTab(): TabType {
        const currentPage = this.routerBackoffice.getParametersString('customers', 'tab.tab.tab') ?? '';
        if (isTab(currentPage)) {
            return currentPage;
        }
        return 'all';
    }

    @computed public get pageState(): PageState {
        return this.pagePerTab.get(this.currentTab);
    }

    @computed public get page(): number {
        return this.pageState.page;
    }

    @computed public get assetFlowsResponse(): Result<AssetFlowsResponseType> | null {
        const currentPage = this.currentTab;
        const operationKind = currentPage === 'into' ? 'incoming' : currentPage === 'out' ? 'outgoing' : undefined;

        const activeTypeFilter =
            this.transactionTypeActiveFilter === 'all' ? undefined : this.transactionTypeActiveFilter;
        const activeStatusFilter =
            this.transactionStatusActiveFilter === 'all' ? undefined : this.transactionStatusActiveFilter;

        return this.userAssetFlows
            .get([
                this.page,
                this.finalRange.startDate.toString(),
                this.finalRange.endDate.toString(),
                activeTypeFilter,
                activeStatusFilter,
                operationKind,
            ])
            .get();
    }

    @computed public get assetFlowsData(): AssetFlowsResponseType | null {
        if (this.assetFlowsResponse !== null) {
            if (this.assetFlowsResponse.type === 'ready') {
                return this.assetFlowsResponse.value;
            }
        }
        return null;
    }

    @computed public get assetFlowsCurrentCount(): number {
        if (this.assetFlowsData === null) {
            return 0;
        }
        return this.assetFlowsData.current;
    }

    @computed public get assetFlowsList(): Array<AssetFlowsItemType> {
        if (this.assetFlowsData !== null) {
            return this.assetFlowsData.elements;
        }
        return [];
    }

    @computed public get isAssetFlowsListLoading(): boolean {
        return this.assetFlowsResponse?.type === 'loading';
    }

    @computed public get isAssetFlowsListTimeout(): boolean {
        return this.assetFlowsResponse?.type === 'error' && this.assetFlowsResponse.message.includes('Timeout');
    }

    @computed public get shouldDisplayTimeoutMessage(): boolean {
        return !this.isAssetFlowsListLoading && this.isAssetFlowsListTimeout;
    }

    @computed public get shouldDisplayEmptyMessage(): boolean {
        return !this.isAssetFlowsListLoading && this.assetFlowsList.length === 0 && !this.isAssetFlowsListTimeout;
    }

    @computed public get assetFlowsListLength(): number {
        return this.assetFlowsList.length;
    }

    @action public onSetInitialPageChange = (): void => {
        this.pageState.page = 1;
    };

    @action public onPageChange = (page: number): void => {
        this.pageState.page = page;
    };

    public handlerStatusType = (type: SpecialColumnType['id'], value: string | undefined): void => {
        const parsedValue = value === undefined ? 'all' : value;
        if (type === 'status') {
            this.filterType.status = parsedValue;
        }

        if (type === 'type') {
            this.filterType.type = parsedValue;
        }
    };

    @action public createObjectForCsv = async (): Promise<Array<CsvDataType> | undefined> => {
        const balanceDeltaSign = this.currentTab === 'into' ? 1 : this.currentTab === 'out' ? -1 : undefined;

        const { startDate, endDate } = this.range;
        const finalEndDate =
            startDate.toISOString() === endDate.toISOString()
                ? (DateTime.from(endDate)?.tz('Europe/London').format('YYYY-MM-DDT23:59:59') ?? '')
                : (DateTime.from(endDate)?.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ss') ?? '');
        const typesOfTransactions = this.filterType.type === 'all' ? undefined : this.filterType.type.split(';');

        const body = {
            sort: '-date',
            startDate: DateTime.from(startDate)?.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ss') ?? '',
            endDate: finalEndDate,
            balanceDeltaSign,
            transactionStatus: this.filterType.status === 'all' ? undefined : this.filterType.status,
            transactionTypes: typesOfTransactions,
        };

        const responseForCsv = await this.sdkStaff.postAssetFlowsCsv({ requestBody: body, user_id: this.userId });
        const csvData = responseForCsv.map((item: CsvDataItemType) => {
            const {
                balanceAfter,
                balanceDelta,
                currency,
                date,
                description,
                finalizedAt,
                freebetCredits,
                gameName,
                id,
                paymentMethod,
                paymentResponse,
                status,
                transactionId,
                type,
            } = item;

            //@ts-expect-error
            const method = paymentMethod?.type === undefined ? '-' : METHOD_LABELS[paymentMethod.type];
            const isFreeBet =
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                freebetCredits !== undefined && freebetCredits !== null ? freebetCredits.length > 0 : false;
            const amount =
                type === 'bet-placement' && isFreeBet ? 'Free' : new Amount(balanceDelta).format(getCurrency(currency));

            return {
                date,
                transactionId: id,
                betId: transactionId,
                method,
                game: gameName ?? '-',
                type,
                status,
                amount,
                balance: new Amount(balanceAfter).format(getCurrency(currency)),
                finalizedAt: finalizedAt === null ? '-' : (DateTime.from(finalizedAt)?.format('YYYY-MM-DD') ?? '-'),
                orderId:
                    paymentResponse !== undefined && paymentResponse !== null ? paymentResponse.providerOrderId : '-',
                bonus: description === undefined ? '-' : description,
            };
        });

        return csvData;
    };

    @action public onGenerateCSV = async (): Promise<void> => {
        if (this.assetFlowsList.length === 0) {
            return;
        }

        const headers = [
            { key: 'date', displayLabel: 'Date' },
            { key: 'transactionId', displayLabel: 'Transaction ID' },
            { key: 'betId', displayLabel: 'Bet ID' },
            { key: 'method', displayLabel: 'Method' },
            { key: 'game', displayLabel: 'Game' },
            { key: 'type', displayLabel: 'Type' },
            { key: 'status', displayLabel: 'Status' },
            { key: 'amount', displayLabel: 'Amount' },
            { key: 'balance', displayLabel: 'Balance' },
            { key: 'finalizedAt', displayLabel: 'Finalized' },
            { key: 'orderId', displayLabel: 'Order ID' },
            { key: 'bonus', displayLabel: 'Bonus' },
        ];

        const options = {
            title: 'Transaction History',
            filename: 'transaction-history',
            showLabels: true,
            columnHeaders: headers,
        };

        const csvExporter = mkConfig(options);
        const csvData = await this.createObjectForCsv();

        if (csvData === undefined) {
            return;
        }
        // @ts-expect-error
        const csv = generateCsv(csvExporter)(csvData);
        download(csvExporter)(csv);
    };
}
